import type {
  CommentPerson,
  CommentSummary,
  CommentThread,
  CommentThreadDetail,
  CommentThreadList,
} from "@/domain/comment";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { detailSlot, isObject } from "../cache/cacheSlots";
import { aggregateSlot, type AggregateDelta } from "../cache/aggregateSlots";
import { defineEntityCache } from "../cache/entityCache";
import {
  byUuid,
  distinctPages,
  viewSlot,
  type ViewRules,
  type ViewShape,
} from "../cache/listViews";
import { optimisticCommentThread, optimisticThread, type CommentEdit } from "./optimistic";

/** An action painted before the server answers, or the thread the server answered with. */
export type CommentPatch =
  | { edit: CommentEdit; actor: CommentPerson | null }
  | { thread: CommentThreadDetail };

type ThreadPage = Pick<CommentThreadList, "threads" | "total_count">;

const page: ViewShape<ThreadPage, CommentThread, string> = {
  isPage: (value): value is ThreadPage =>
    isObject(value) && Array.isArray(value.threads) && typeof value.total_count === "number",
  records: (current) => current.threads,
  withRecords: (current, threads, countDelta) => ({
    ...current,
    threads,
    total_count: current.total_count + countDelta,
  }),
  idOf: (thread) => thread.uuid,
  total: (current) => current.total_count,
};

export const distinctThreadPages = distinctPages(page);

function isThreadDetail(value: unknown): value is CommentThreadDetail {
  return isObject(value) && typeof value.uuid === "string" && Array.isArray(value.messages);
}

const rowOf = ({ messages: _messages, ...thread }: CommentThreadDetail): CommentThread => thread;

const merge = (thread: CommentThread, patch: CommentPatch): CommentThread =>
  "thread" in patch ? rowOf(patch.thread) : optimisticThread(thread, patch.edit, patch.actor);

const isOpen = (thread: CommentThread) => thread.resolved_at === null;

export const commentRules: ViewRules<CommentThread> = {
  accepts: {
    status: (value, thread) => value === undefined || (value === "open") === isOpen(thread),
    is_read: (value, thread) => typeof value !== "boolean" || value === thread.is_read,
  },
  order: { last_message_at: (thread) => Date.parse(thread.last_message_at) },
  tieBreak: byUuid("desc", (thread) => thread.uuid),
};

const THREAD_ORDER = [{ key: "last_message_at", direction: "desc" }] as const;

function isSummary(value: unknown): value is CommentSummary {
  return (
    isObject(value) &&
    typeof value.open_count === "number" &&
    typeof value.unread_count === "number"
  );
}

function summaryDelta(data: CommentSummary, delta: AggregateDelta<CommentThread>) {
  const weight = (thread: CommentThread | undefined) => ({
    open: thread && isOpen(thread) ? 1 : 0,
    unread: thread && isOpen(thread) && !thread.is_read ? 1 : 0,
  });
  const added = weight(delta.added);
  const removed = weight(delta.removed);
  const open_count = data.open_count + added.open - removed.open;
  const unread_count = data.unread_count + added.unread - removed.unread;
  return open_count === data.open_count && unread_count === data.unread_count
    ? data
    : { ...data, open_count, unread_count };
}

/** Threads live in lists split by status (newest message first), their summary and the open thread's detail. */
export const commentCache = defineEntityCache<string, CommentPatch, CommentThread>({
  entity: InvalidationEntity.COMMENTS,
  row: merge,
  slots: (orgId) => [
    viewSlot({
      prefix: queryKeys.comments.lists(orgId),
      shape: page,
      merge,
      parse: (key) => {
        const view = queryKeys.comments.parseList(key);
        return view && { ...view, sort: THREAD_ORDER };
      },
      rules: commentRules,
    }),
    aggregateSlot({
      prefix: queryKeys.comments.summaries(orgId),
      rules: commentRules,
      parse: queryKeys.comments.parseSummary,
      isData: isSummary,
      apply: summaryDelta,
    }),
    detailSlot({
      prefix: queryKeys.comments.details(orgId),
      isRecord: isThreadDetail,
      idOf: (thread) => thread.uuid,
      merge: (thread, patch) =>
        "thread" in patch ? patch.thread : optimisticCommentThread(thread, patch.edit, patch.actor),
    }),
  ],
  records: (orgId, uuid) => [queryKeys.comments.detail(orgId, uuid)],
});
